import React from "react";
import { useNavigate } from "react-router-dom";
import { ModuleBoundary } from "../ModuleBoundary";
import { Tile } from "../components/Tile";

/**
 * Tile renderers — one per module.
 *
 * Each tile is wrapped in <ModuleBoundary moduleName="...">. If the
 * module is not in the user's capability map, the tile renders nothing
 * and the grid auto-flows around it (L1 + L9).
 *
 * Phase 2 keeps tile content deliberately minimal — every tile passes the
 * 5-second comprehension test (one concept · one metric · one action).
 * Phase 4+ may enrich tiles with real-time KPIs against the freshness
 * tier vocabulary (L8.1).
 */

export const ReviewShieldTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="review_shield">
            <Tile
                moduleName="review_shield"
                icon="shield-check"
                title="Review Shield"
                metric="Active"
                context="Guarding your guest-facing reviews."
                severity="operational"
                onTap={() => nav("/review-shield-results")}
                testId="ob-tile-review_shield"
            />
        </ModuleBoundary>
    );
};

export const ReviewShieldIntelligenceTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="review_shield">
            <Tile
                moduleName="review_shield"
                icon="shield-alert"
                title="Review Shield Intelligence"
                metric="Triage"
                context="Operator-grade triage of guest feedback."
                severity="operational"
                onTap={() => nav("/operational-base/review-shield")}
                testId="ob-tile-review_shield_intelligence"
            />
        </ModuleBoundary>
    );
};

export const SurveysTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="surveys">
            <Tile
                moduleName="surveys"
                icon="clipboard-list"
                title="Surveys"
                metric="Listening"
                context="Collecting structured guest sentiment."
                severity="operational"
                onTap={() => nav("/surveys")}
                testId="ob-tile-surveys"
            />
        </ModuleBoundary>
    );
};

export const VouchersTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="vouchers">
            <Tile
                moduleName="vouchers"
                icon="ticket"
                title="Vouchers"
                metric="Ready"
                context="Recovery and recognition rewards."
                severity="operational"
                onTap={() => nav("/voucher-reports")}
                testId="ob-tile-vouchers"
            />
        </ModuleBoundary>
    );
};

export const RecoveryTile = () => {
    return (
        <ModuleBoundary moduleName="recovery">
            <Tile
                moduleName="recovery"
                icon="life-buoy"
                title="Recovery"
                metric="Calm"
                context="Open recovery flows surface here when guests need attention."
                severity="operational"
                testId="ob-tile-recovery"
            />
        </ModuleBoundary>
    );
};

export const AnalyticsTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="analytics">
            <Tile
                moduleName="analytics"
                icon="bar-chart-3"
                title="Operational Briefing"
                metric="Insight"
                context="What deserves attention across your network."
                severity="info"
                onTap={() => nav("/operational-base/analytics")}
                testId="ob-tile-analytics"
            />
        </ModuleBoundary>
    );
};

export const LocationsTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="locations">
            <Tile
                moduleName="locations"
                icon="map-pin"
                title="Locations"
                metric="Network"
                context="Each site's operational presence."
                severity="info"
                onTap={() => nav("/locations")}
                testId="ob-tile-locations"
            />
        </ModuleBoundary>
    );
};

export const BrandingTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="branding">
            <Tile
                moduleName="branding"
                icon="palette"
                title="Branding"
                metric="Aligned"
                context="Visual identity across your environment."
                severity="info"
                onTap={() => nav("/admin/brands")}
                testId="ob-tile-branding"
            />
        </ModuleBoundary>
    );
};

export const NotificationsTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="notifications">
            <Tile
                moduleName="notifications"
                icon="bell"
                title="Notifications"
                metric="Quiet"
                context="Operational alerts route here when needed."
                severity="info"
                onTap={() => nav("/admin/notifications")}
                testId="ob-tile-notifications"
            />
        </ModuleBoundary>
    );
};

export const PermissionsTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="permissions">
            <Tile
                moduleName="permissions"
                icon="key-round"
                title="Permissions"
                metric="Governed"
                context="Who can do what, by role and scope."
                severity="info"
                onTap={() => nav("/admin/permissions")}
                testId="ob-tile-permissions"
            />
        </ModuleBoundary>
    );
};

/**
 * ModulesTile — Phase 3 calm provisioning entry. Always wrapped in
 * `operational_base` boundary; visibility is further filtered to
 * super-admins on the home page (governance category = meta).
 */
export const ModulesTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="operational_base">
            <Tile
                moduleName="operational_base"
                icon="layout-grid"
                title="Modules"
                metric="Adaptive"
                context="What this organization has and does not have."
                severity="operational"
                onTap={() => nav("/operational-base/modules")}
                testId="ob-tile-modules"
            />
        </ModuleBoundary>
    );
};

const TemplatesTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="operational_base">
            <Tile
                moduleName="operational_base"
                icon="layers"
                title="Templates"
                metric="Playbook"
                context="Push operational baselines across the hierarchy."
                severity="operational"
                onTap={() => nav("/operational-base/templates")}
                testId="ob-tile-templates"
            />
        </ModuleBoundary>
    );
};

const WizardTile = () => {
    const nav = useNavigate();
    return (
        <ModuleBoundary moduleName="operational_base">
            <Tile
                moduleName="operational_base"
                icon="wand-2"
                title="Setup Wizard"
                metric="Guided"
                context="Provision a new region, locations, and baseline in minutes."
                severity="operational"
                onTap={() => nav("/operational-base/wizard")}
                testId="ob-tile-wizard"
            />
        </ModuleBoundary>
    );
};

/**
 * Returns the Phase 2 tile set in priority order. The home page renders
 * the first 5–6 visible tiles (Binding Rule #7) and reserves remaining
 * modules for the deeper navigation surface.
 */
export const TILES_IN_ORDER = [
    ReviewShieldTile,
    SurveysTile,
    VouchersTile,
    RecoveryTile,
    AnalyticsTile,
    LocationsTile,
    BrandingTile,
    NotificationsTile,
    PermissionsTile,
    ModulesTile,
    TemplatesTile,
    WizardTile,
    ReviewShieldIntelligenceTile,
];
